import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MapPin, Pencil, Star, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { api } from '@/api/client';

export default function ReturnAddressCard({ address, onEdit }) {
  const queryClient = useQueryClient();

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['returnAddresses'] });

  const deleteMutation = useMutation({
    mutationFn: () => api.entities.ReturnAddress.delete(address.id),
    onSuccess: () => {
      invalidate();
      toast.success('Return address removed');
    },
    onError: (error) => toast.error(error.message ?? 'Failed to remove address'),
  });

  // One request. The API moves the default in a transaction, so there is no
  // window where the shop has two defaults or none.
  const setDefaultMutation = useMutation({
    mutationFn: () => api.entities.ReturnAddress.update(address.id, { isDefault: true }),
    onSuccess: () => {
      invalidate();
      toast.success('Default return address updated');
    },
    onError: (error) => toast.error(error.message ?? 'Failed to set default'),
  });

  const handleDelete = () => {
    if (window.confirm(`Remove return address "${address.name}"?`)) deleteMutation.mutate();
  };

  return (
    <Card className="border-slate-200 hover:border-red-300 transition-colors">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center shrink-0">
              <MapPin className="w-5 h-5 text-red-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h4 className="font-semibold text-slate-900">{address.name}</h4>
                {address.isDefault && (
                  <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300 gap-1 hover:bg-yellow-100">
                    <Star className="w-3 h-3 fill-yellow-600" />
                    Default
                  </Badge>
                )}
              </div>
              {address.contactName && (
                <p className="text-sm text-slate-600">{address.contactName}</p>
              )}
              <p className="text-sm text-slate-600">{address.address1}</p>
              {address.address2 && <p className="text-sm text-slate-600">{address.address2}</p>}
              <p className="text-sm text-slate-600">
                {address.city}, {address.state} {address.zip}
              </p>
              <p className="text-sm text-slate-500">{address.phone}</p>

              {!address.isDefault && (
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => setDefaultMutation.mutate()}
                  disabled={setDefaultMutation.isPending}
                  className="h-auto p-0 text-xs text-blue-600 hover:text-blue-700 mt-1"
                >
                  Set as default
                </Button>
              )}
            </div>
          </div>

          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEdit(address)}
              className="hover:bg-red-50 hover:text-red-600"
              aria-label={`Edit ${address.name}`}
            >
              <Pencil className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="hover:bg-red-50 hover:text-red-600"
              aria-label={`Remove ${address.name}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
